import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Button, Card, CardHeader, Input } from '@/components/common';
import { getErrorMessage } from '@/services/api';
import { assistantService, type AssistantKey, type CreatedAssistantKey } from '@/services/assistants';
import { formatDateTime } from '@/utils';

/**
 * Settings → AI assistants (Messaging Plan 4). A personal key lets an AI assistant run the quote journey
 * over MCP. The full key is shown once — the server keeps only its hash — so this panel shows it in a
 * one-time box and never again. Loading, failure and "none yet" are three different states.
 */

function when(value: string | null): string {
  return value ? formatDateTime(value) : 'Never';
}

export function AssistantKeysPanel() {
  const queryClient = useQueryClient();
  const server = useQuery({ queryKey: ['assistant-server'], queryFn: () => assistantService.getServer() });
  const keys = useQuery({ queryKey: ['assistant-keys'], queryFn: () => assistantService.listKeys() });
  const [name, setName] = useState('');
  const [created, setCreated] = useState<CreatedAssistantKey | null>(null);

  const create = useMutation({
    mutationFn: (keyName: string) => assistantService.createKey(keyName),
    onSuccess: (key) => {
      setCreated(key);
      setName('');
      queryClient.invalidateQueries({ queryKey: ['assistant-keys'] });
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
  const revoke = useMutation({
    mutationFn: (id: string) => assistantService.revokeKey(id),
    onSuccess: () => {
      toast.success('Key revoked');
      queryClient.invalidateQueries({ queryKey: ['assistant-keys'] });
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const rows: AssistantKey[] = Array.isArray(keys.data) ? keys.data : [];
  const canCreate = Boolean(server.data?.can_create);

  return (
    <Card className="p-6 space-y-6">
      <CardHeader
        title="AI assistants"
        subtitle="Let an AI assistant (for example Claude) get live quotes and send your customers the secure form, using a personal key."
      />
      {server.isError && <p role="alert" className="text-sm text-red-600">Could not load the assistant settings. Try again.</p>}
      {server.data && (
        <div className="space-y-1 text-sm text-gray-700 dark:text-gray-300">
          <p>Server address (MCP, streamable HTTP):</p>
          <code className="block break-all rounded bg-gray-100 px-2 py-1 dark:bg-gray-800" data-testid="mcp-url">
            {server.data.url}
          </code>
          {!canCreate && server.data.reason && <p className="text-amber-700 dark:text-amber-400">{server.data.reason}</p>}
        </div>
      )}
      <p className="text-sm text-gray-600 dark:text-gray-400">
        The assistant never sees PAN, Aadhaar or payment details: your customer enters those in the secure form.
        The prices it reads are live insurer prices.
      </p>

      {created && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-500/40 dark:bg-amber-500/10">
          <p className="text-sm font-medium text-gray-900 dark:text-white">Copy this key now — it is shown only once</p>
          <code className="mt-2 block break-all font-mono text-sm text-gray-900 dark:text-gray-100" data-testid="new-key">
            {created.key}
          </code>
          <div className="mt-3 flex gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                navigator.clipboard?.writeText(created.key);
                toast.success('Key copied');
              }}
            >
              Copy key
            </Button>
            <Button onClick={() => setCreated(null)}>Done</Button>
          </div>
        </div>
      )}

      <form
        className="flex flex-col gap-2 sm:flex-row sm:items-end"
        onSubmit={(event) => {
          event.preventDefault();
          if (name.trim()) create.mutate(name.trim());
        }}
      >
        <div className="flex-1">
          <Input
            id="assistant-key-name"
            label="Key name"
            value={name}
            maxLength={100}
            placeholder="e.g. Claude on my laptop"
            onChange={(event) => setName(event.target.value)}
          />
        </div>
        <Button type="submit" disabled={!canCreate || !name.trim() || create.isPending}>Create key</Button>
      </form>

      <section aria-label="Your keys">
        {keys.isLoading && <p className="text-sm text-gray-500">Loading your keys…</p>}
        {keys.isError && <p role="alert" className="text-sm text-red-600">Could not load your keys. Try again.</p>}
        {keys.isSuccess && rows.length === 0 && <p className="text-sm text-gray-500">No keys yet.</p>}
        {rows.length > 0 && (
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {rows.map((key) => (
              <li key={key.id} className="flex items-center justify-between gap-4 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{key.name}</p>
                  <p className="truncate font-mono text-xs text-gray-500">{key.display_key}</p>
                  <p className="text-xs text-gray-500">Last used: {when(key.last_used_at)}</p>
                </div>
                {key.is_active ? (
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => {
                      if (window.confirm(`Revoke "${key.name}"? Assistants using it stop working at once.`)) {
                        revoke.mutate(key.id);
                      }
                    }}
                  >
                    Revoke
                  </Button>
                ) : (
                  <span className="text-xs text-gray-500">Revoked</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </Card>
  );
}
